// Batch 4 of the Compare tab content (English and Italian).
// Same structure as the other batches.

export const BATCH_4 = [
    {
        id: 'time',
        level: 'A1',
        title: { en: 'Telling the time', it: "Dire l'ora" },
        summary: {
            en: 'Asking and saying what time it is.',
            it: "Chiedere e dire che ora è.",
        },
        forms: {
            english: [
                {
                    name: "It is ... o'clock",
                    formula: {
                        en: "It is five o'clock. Half past five. A quarter past five. A quarter to six. Times are often given with am or pm.",
                        it: "It is five o'clock. Half past five. A quarter past five. A quarter to six. Spesso si usano am e pm.",
                    },
                },
            ],
            italian: [
                {
                    name: 'Sono le ... / È l\'una',
                    formula: {
                        en: 'Sono le + hour: Sono le cinque. Only one o\'clock, midday and midnight are singular: È l\'una, È mezzogiorno, È mezzanotte. Add minutes with e: Sono le cinque e dieci. Take minutes away with meno: Sono le sei meno dieci. E mezza = half past, e un quarto = a quarter past, meno un quarto = a quarter to.',
                        it: 'Sono le + ora: Sono le cinque. Solo l\'una, mezzogiorno e mezzanotte sono singolari: È l\'una, È mezzogiorno, È mezzanotte. Aggiungi i minuti con e: Sono le cinque e dieci. Togli i minuti con meno: Sono le sei meno dieci. E mezza, e un quarto, meno un quarto.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'What time is it?', tag: 'What time is it?' },
                italian: { text: 'Che ore sono?', tag: 'Che ore sono?' },
            },
            {
                english: { text: "It is five o'clock.", tag: "It is ... o'clock" },
                italian: { text: 'Sono le cinque.', tag: 'Sono le + hour' },
            },
            {
                english: { text: "It is one o'clock.", tag: "It is ... o'clock" },
                italian: { text: "È l'una.", tag: "È l'una (singular)" },
            },
            {
                english: { text: 'It is half past five.', tag: 'half past' },
                italian: { text: 'Sono le cinque e mezza.', tag: 'e mezza' },
            },
            {
                english: { text: 'It is a quarter to six.', tag: 'a quarter to' },
                italian: { text: 'Sono le sei meno un quarto.', tag: 'meno un quarto' },
            },
            {
                english: { text: 'What time does the bank open?', tag: 'What time ...?' },
                italian: { text: 'A che ora apre la banca?', tag: 'A che ora ...?' },
            },
            {
                english: { text: 'The train leaves at 5:30 pm.', tag: 'at + time' },
                italian: { text: 'Il treno parte alle diciassette e trenta.', tag: 'alle + 24-hour time' },
            },
        ],
        notes: [
            {
                en: "In Italian the hour is plural, because it literally says 'they are the five': 'Sono le cinque'. Only one o'clock, midday and midnight are singular: 'È l'una'.",
                it: "In italiano l'ora è plurale, perché dice letteralmente 'sono le cinque'. Solo l'una, mezzogiorno e mezzanotte sono singolari: 'È l'una'.",
            },
            {
                en: "For 'at' with a time, Italian uses a + the article: 'alle cinque', 'all'una', 'a mezzogiorno'.",
                it: "Per 'at' con un orario, l'italiano usa a + l'articolo: 'alle cinque', 'all'una', 'a mezzogiorno'.",
            },
            {
                en: "Italians use the 24-hour clock for timetables and appointments (alle 17:30). There is no am or pm. In conversation you say di mattina, del pomeriggio or di sera: 'Sono le cinque del pomeriggio'.",
                it: "Gli italiani usano le 24 ore per orari e appuntamenti (alle 17:30). Non esistono am e pm. Nel parlato si dice di mattina, del pomeriggio o di sera: 'Sono le cinque del pomeriggio'.",
            },
        ],
    },
    {
        id: 'dates',
        level: 'A1',
        title: { en: 'Days, months and dates', it: 'Giorni, mesi e date' },
        summary: {
            en: 'Saying the day, the date and the year.',
            it: 'Dire il giorno, la data e l\'anno.',
        },
        forms: {
            english: [
                {
                    name: 'Month + day or day + month',
                    formula: {
                        en: 'October 24 (US) or 24 October (UK). You can say the 24th. Days and months start with a capital letter.',
                        it: 'October 24 (USA) o 24 October (UK). Si può dire the 24th. I giorni e i mesi iniziano con la maiuscola.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Il + day + month',
                    formula: {
                        en: 'il 24 ottobre. Always day before month, with the article il. Cardinal numbers, except for the first: il primo maggio. Days and months do not start with a capital letter. Year: nel 2026.',
                        it: 'il 24 ottobre. Sempre il giorno prima del mese, con l\'articolo il. Numeri cardinali, tranne per il primo: il primo maggio. Giorni e mesi non hanno la maiuscola. Anno: nel 2026.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'Today is Saturday.', tag: 'Day of the week' },
                italian: { text: 'Oggi è sabato.', tag: 'No capital letter' },
            },
            {
                english: { text: 'Today is October 24.', tag: 'Month + day' },
                italian: { text: 'Oggi è il 24 ottobre.', tag: 'Il + day + month' },
            },
            {
                english: { text: 'My birthday is on March 3.', tag: 'on + date' },
                italian: { text: 'Il mio compleanno è il 3 marzo.', tag: 'il + date' },
            },
            {
                english: { text: 'The first of May is a holiday.', tag: 'The first' },
                italian: { text: 'Il primo maggio è festa.', tag: 'Il primo (ordinal)' },
            },
            {
                english: { text: 'I was in Rome in 2020.', tag: 'in + year' },
                italian: { text: 'Ero a Roma nel 2020.', tag: 'in + il = nel' },
            },
            {
                english: { text: 'I go to the gym on Saturdays.', tag: 'on + plural day' },
                italian: { text: 'Vado in palestra il sabato.', tag: 'il + day (habit)' },
            },
        ],
        notes: [
            {
                en: 'Italian days of the week and months are not capitalized: sabato, ottobre. English always capitalizes them.',
                it: "I giorni della settimana e i mesi italiani non si scrivono con la maiuscola: sabato, ottobre. L'inglese li scrive sempre con la maiuscola.",
            },
            {
                en: "Italian uses ordinal numbers only for the first of the month (il primo maggio). All other days are cardinal: il due maggio, not 'il secondo maggio'.",
                it: "L'italiano usa il numero ordinale solo per il primo del mese (il primo maggio). Tutti gli altri giorni sono cardinali: il due maggio, non 'il secondo maggio'.",
            },
            {
                en: "For a habit, 'il sabato' means every Saturday. For one specific Saturday, drop the article: 'Sabato vado al mare'.",
                it: "Per un'abitudine, 'il sabato' vuol dire ogni sabato. Per un sabato preciso, togli l'articolo: 'Sabato vado al mare'.",
            },
        ],
    },
    {
        id: 'connectors',
        level: 'A2',
        title: { en: 'Joining ideas (and, but, because, so)', it: 'Collegare le idee (e, ma, perché, quindi)' },
        summary: {
            en: 'Linking words that join two parts of a sentence.',
            it: 'Parole che collegano due parti di una frase.',
        },
        forms: {
            english: [
                {
                    name: 'And, but, because, so, although, when',
                    formula: {
                        en: 'They join two parts of a sentence. After when, English uses the present for a future event: When I arrive, I will call you.',
                        it: 'Collegano due parti di una frase. Dopo when, l\'inglese usa il presente per un evento futuro: When I arrive, I will call you.',
                    },
                },
            ],
            italian: [
                {
                    name: 'E, ma, perché, quindi, anche se, quando',
                    formula: {
                        en: 'e (and), ma (but), perché (because, and also why), quindi (so), anche se (even though), quando (when). After quando, the future is used for a future event: Quando arriverò, ti chiamerò.',
                        it: 'e, ma, perché (anche "why"), quindi, anche se, quando. Dopo quando si usa il futuro per un evento futuro: Quando arriverò, ti chiamerò.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'She is tired because she worked a lot.', tag: 'because' },
                italian: { text: 'È stanca perché ha lavorato molto.', tag: 'perché' },
            },
            {
                english: { text: 'I like it, but it is expensive.', tag: 'but' },
                italian: { text: 'Mi piace, ma è caro.', tag: 'ma' },
            },
            {
                english: { text: 'It was raining, so we stayed home.', tag: 'so' },
                italian: { text: 'Pioveva, quindi siamo rimasti a casa.', tag: 'quindi' },
            },
            {
                english: { text: 'Even though it is cold, I go out.', tag: 'even though' },
                italian: { text: 'Anche se fa freddo, esco.', tag: 'anche se + indicative' },
            },
            {
                english: { text: 'When I arrive, I will call you.', tag: 'When + present' },
                italian: { text: 'Quando arriverò, ti chiamerò.', tag: 'Quando + futuro' },
            },
        ],
        notes: [
            {
                en: "Perché means both 'why' and 'because': 'Perché sei in ritardo?' 'Perché c'era traffico.'",
                it: "Perché vuol dire sia 'why' sia 'because': 'Perché sei in ritardo?' 'Perché c'era traffico.'",
            },
            {
                en: "After when and as soon as, English uses the present for the future ('When I arrive'). Italian uses the future: 'Quando arriverò'.",
                it: "Dopo when e as soon as, l'inglese usa il presente per il futuro ('When I arrive'). L'italiano usa il futuro: 'Quando arriverò'.",
            },
            {
                en: 'Anche se (even though) takes the normal indicative. Benché, with the same meaning, needs the congiuntivo: Benché faccia freddo, esco.',
                it: 'Anche se prende il normale indicativo. Benché, con lo stesso significato, vuole il congiuntivo: Benché faccia freddo, esco.',
            },
        ],
    },
    {
        id: 'indefinites',
        level: 'A2',
        title: { en: 'Something, nothing, someone', it: 'Qualcosa, niente, qualcuno' },
        summary: {
            en: 'Words for unknown or no people and things.',
            it: 'Parole per persone e cose sconosciute o assenti.',
        },
        forms: {
            english: [
                {
                    name: 'some-, any-, no-, every-',
                    formula: {
                        en: 'something, someone for positive sentences. anything, anyone for questions and negatives (I don\'t need anything). nothing, no one for a negative meaning without not. everything, everyone for all.',
                        it: 'something, someone per le frasi positive. anything, anyone per domande e negazioni (I don\'t need anything). nothing, no one per un significato negativo senza not. everything, everyone per tutto.',
                    },
                },
            ],
            italian: [
                {
                    name: 'qualcosa, niente, qualcuno, nessuno, tutto',
                    formula: {
                        en: 'qualcosa (something), qualcuno (someone), niente or nulla (nothing), nessuno (no one), tutto (everything), tutti (everyone), ogni + singular noun (every). Niente and nessuno need non before the verb when they come after it.',
                        it: 'qualcosa, qualcuno, niente o nulla, nessuno, tutto, tutti, ogni + nome singolare. Niente e nessuno richiedono non prima del verbo quando vengono dopo.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I need something.', tag: 'something' },
                italian: { text: 'Ho bisogno di qualcosa.', tag: 'qualcosa' },
            },
            {
                english: { text: "I don't need anything.", tag: 'anything' },
                italian: { text: 'Non ho bisogno di niente.', tag: 'non ... niente' },
            },
            {
                english: { text: 'Someone is calling.', tag: 'someone' },
                italian: { text: 'Qualcuno chiama.', tag: 'qualcuno' },
            },
            {
                english: { text: 'There is nobody here.', tag: 'nobody' },
                italian: { text: "Non c'è nessuno.", tag: 'non ... nessuno' },
            },
            {
                english: { text: 'Everyone is here.', tag: 'everyone (singular verb)' },
                italian: { text: 'Sono tutti qui.', tag: 'tutti (plural verb)' },
            },
            {
                english: { text: 'Everything is fine.', tag: 'everything' },
                italian: { text: 'Va tutto bene.', tag: 'tutto' },
            },
            {
                english: { text: 'I go to the gym every day.', tag: 'every + singular' },
                italian: { text: 'Vado in palestra ogni giorno.', tag: 'ogni + singular' },
            },
        ],
        notes: [
            {
                en: "English changes some- to any- in questions and negatives. Italian uses qualcosa in positive sentences and questions, and non ... niente for negatives: 'Non ho bisogno di niente'.",
                it: "L'inglese cambia some- in any- nelle domande e nelle negazioni. L'italiano usa qualcosa nelle frasi positive e nelle domande, e non ... niente per le negazioni: 'Non ho bisogno di niente'.",
            },
            {
                en: "'Everyone' is singular in English (Everyone is here) but 'tutti' is plural in Italian (Sono tutti qui).",
                it: "'Everyone' è singolare in inglese (Everyone is here) ma 'tutti' è plurale in italiano (Sono tutti qui).",
            },
            {
                en: "Ogni is always followed by a singular noun and never changes: ogni giorno, ogni settimana.",
                it: "Ogni è sempre seguito da un nome singolare e non cambia mai: ogni giorno, ogni settimana.",
            },
        ],
    },
    {
        id: 'quantity',
        level: 'A2',
        title: { en: 'Some, much, many', it: "Un po', molto, tanto" },
        summary: {
            en: 'Saying how much or how many.',
            it: 'Dire quanto o quanti.',
        },
        forms: {
            english: [
                {
                    name: 'some / any / much / many / a lot of',
                    formula: {
                        en: 'some and any with plurals and uncountable nouns. much with uncountable nouns (much time), many with countable nouns (many books). a lot of works with both.',
                        it: 'some e any con i plurali e i nomi non numerabili. much con i nomi non numerabili (much time), many con quelli numerabili (many books). a lot of va bene per entrambi.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Partitive and molto, tanto, troppo, quanto',
                    formula: {
                        en: "For 'some', use di + article: dell'acqua, dei libri, delle amiche. Molto, tanto, poco, troppo and quanto before a noun match it: molta gente, molti libri, troppo zucchero. Before an adjective or verb they never change: molto stanca, lavora molto.",
                        it: "Per 'some' si usa di + articolo: dell'acqua, dei libri, delle amiche. Molto, tanto, poco, troppo e quanto davanti a un nome concordano con esso: molta gente, molti libri, troppo zucchero. Davanti a un aggettivo o a un verbo non cambiano: molto stanca, lavora molto.",
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I want some water.', tag: 'some' },
                italian: { text: "Voglio dell'acqua.", tag: "Partitive: dell'" },
            },
            {
                english: { text: 'I have some friends in Rome.', tag: 'some' },
                italian: { text: 'Ho degli amici a Roma.', tag: 'Partitive: degli' },
            },
            {
                english: { text: 'He has a lot of time.', tag: 'a lot of' },
                italian: { text: 'Ha molto tempo.', tag: 'molto (matches tempo)' },
            },
            {
                english: { text: 'There are a lot of people.', tag: 'a lot of + plural' },
                italian: { text: "C'è molta gente.", tag: 'molta + singular' },
            },
            {
                english: { text: 'How many books do you have?', tag: 'How many' },
                italian: { text: 'Quanti libri hai?', tag: 'Quanti (matches libri)' },
            },
            {
                english: { text: 'Too much sugar.', tag: 'too much' },
                italian: { text: 'Troppo zucchero.', tag: 'troppo (matches zucchero)' },
            },
            {
                english: { text: 'She is very tired.', tag: 'very' },
                italian: { text: 'È molto stanca.', tag: 'molto (adverb: no change)' },
            },
        ],
        notes: [
            {
                en: "'Gente' (people) is singular in Italian: 'C'è molta gente', not 'Ci sono molte genti'. English 'people' is plural.",
                it: "'Gente' è singolare in italiano: 'C'è molta gente', non 'Ci sono molte genti'. L'inglese 'people' è plurale.",
            },
            {
                en: "Molto has two jobs. Before a noun it is an adjective and matches it (molti libri, molta acqua). Before an adjective or verb it is an adverb and never changes (molto stanca, parla molto).",
                it: "Molto ha due funzioni. Davanti a un nome è un aggettivo e concorda (molti libri, molta acqua). Davanti a un aggettivo o a un verbo è un avverbio e non cambia (molto stanca, parla molto).",
            },
            {
                en: "The partitive (dei, delle, dell') disappears in negative sentences: 'Ho degli amici' becomes 'Non ho amici'.",
                it: "Il partitivo (dei, delle, dell') scompare nelle frasi negative: 'Ho degli amici' diventa 'Non ho amici'.",
            },
        ],
    },
    {
        id: 'ci-ne',
        level: 'B1',
        title: { en: 'Ci and ne', it: 'Ci e ne' },
        summary: {
            en: 'Two small Italian words with no single English equivalent.',
            it: 'Due piccole parole italiane che non hanno un equivalente inglese.',
        },
        forms: {
            english: [
                {
                    name: 'There, it, of it, some',
                    formula: {
                        en: "English has no matching word. It uses there (I am going there), it or about it (I am thinking about it) or just leaves the idea out (I have two).",
                        it: "L'inglese non ha una parola corrispondente. Usa there (I am going there), it o about it (I am thinking about it) oppure sottintende l'idea (I have two).",
                    },
                },
            ],
            italian: [
                {
                    name: 'Ci',
                    formula: {
                        en: 'Ci = there, or to / in / at + that. It replaces a place or a phrase starting with a or in: Ci vado (I go there). Non ci credo (I don\'t believe it).',
                        it: 'Ci = lì, oppure a / in + quello. Sostituisce un luogo o un complemento introdotto da a o in: Ci vado. Non ci credo.',
                    },
                },
                {
                    name: 'Ne',
                    formula: {
                        en: 'Ne = of it, of them, about it, from there. It replaces a phrase starting with di or da, and also a quantity: Ne parliamo. Ne ho due. Ne vuoi?',
                        it: 'Ne = di quello, di loro, da lì. Sostituisce un complemento introdotto da di o da, e anche una quantità: Ne parliamo. Ne ho due. Ne vuoi?',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I am going there.', tag: 'there' },
                italian: { text: 'Ci vado.', tag: 'ci = there' },
            },
            {
                english: { text: "I don't believe it.", tag: 'it' },
                italian: { text: 'Non ci credo.', tag: 'ci = in it' },
            },
            {
                english: { text: 'I am thinking about it.', tag: 'about it' },
                italian: { text: 'Ci penso.', tag: 'ci = about it' },
            },
            {
                english: { text: 'We are talking about it.', tag: 'about it' },
                italian: { text: 'Ne parliamo.', tag: 'ne = about it' },
            },
            {
                english: { text: 'Do you want some?', tag: 'some' },
                italian: { text: 'Ne vuoi?', tag: 'ne = some of it' },
            },
            {
                english: { text: 'I have two.', tag: 'No pronoun' },
                italian: { text: 'Ne ho due.', tag: 'ne = of them' },
            },
            {
                english: { text: 'I want to go there.', tag: 'there' },
                italian: { text: 'Voglio andarci.', tag: 'Attached to the infinitive' },
            },
        ],
        notes: [
            {
                en: "Ci and ne replace something that has already been mentioned. Ci stands for a, in or at + that. Ne stands for di or da + that, and for a quantity.",
                it: "Ci e ne sostituiscono qualcosa che è già stato detto. Ci sta per a, in + quello. Ne sta per di o da + quello, e per una quantità.",
            },
            {
                en: "When the answer is a quantity, Italian normally adds ne: 'Quanti libri hai?' 'Ne ho due.' English just says 'I have two.'",
                it: "Quando la risposta è una quantità, l'italiano di solito aggiunge ne: 'Quanti libri hai?' 'Ne ho due.' L'inglese dice solo 'I have two.'",
            },
            {
                en: 'Like other small pronouns, ci and ne go before the conjugated verb, or attach to the end of an infinitive: Ci vado, Voglio andarci.',
                it: 'Come gli altri pronomi piccoli, ci e ne vanno prima del verbo coniugato, oppure si attaccano alla fine di un infinito: Ci vado, Voglio andarci.',
            },
        ],
    },
    {
        id: 'perfect-tenses',
        level: 'B1',
        title: { en: 'Before another moment', it: 'Prima di un altro momento' },
        summary: {
            en: 'Something that happened before another past or future moment.',
            it: 'Qualcosa che è successo prima di un altro momento passato o futuro.',
        },
        forms: {
            english: [
                {
                    name: 'Past perfect',
                    formula: {
                        en: 'had + past participle: When we arrived, she had already left.',
                        it: 'had + participio passato: When we arrived, she had already left.',
                    },
                },
                {
                    name: 'Future perfect',
                    formula: {
                        en: 'will have + past participle: By tomorrow I will have finished.',
                        it: 'will have + participio passato: By tomorrow I will have finished.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Trapassato prossimo',
                    formula: {
                        en: 'avere / essere in the imperfetto + participle: avevo finito, era partita.',
                        it: 'avere / essere all\'imperfetto + participio: avevo finito, era partita.',
                    },
                },
                {
                    name: 'Futuro anteriore',
                    formula: {
                        en: 'avere / essere in the futuro + participle: avrò finito, sarà partita. It is also used to guess about the past: Avrà dimenticato.',
                        it: 'avere / essere al futuro + participio: avrò finito, sarà partita. Si usa anche per fare ipotesi sul passato: Avrà dimenticato.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'When we arrived, she had already left.', tag: 'Past perfect' },
                italian: { text: 'Quando siamo arrivati, era già partita.', tag: 'Trapassato prossimo' },
            },
            {
                english: { text: 'I had never seen the sea.', tag: 'Past perfect' },
                italian: { text: 'Non avevo mai visto il mare.', tag: 'Trapassato prossimo' },
            },
            {
                english: { text: 'We had finished before noon.', tag: 'Past perfect' },
                italian: { text: 'Avevamo finito prima di mezzogiorno.', tag: 'Trapassato prossimo' },
            },
            {
                english: { text: 'By tomorrow I will have finished.', tag: 'Future perfect' },
                italian: { text: 'Entro domani avrò finito.', tag: 'Futuro anteriore' },
            },
            {
                english: { text: 'When you arrive, I will have eaten.', tag: 'Future perfect' },
                italian: { text: 'Quando arriverai, avrò già mangiato.', tag: 'Futuro anteriore' },
            },
            {
                english: { text: 'He has probably forgotten.', tag: 'probably + present perfect' },
                italian: { text: 'Avrà dimenticato.', tag: 'Futuro anteriore (a guess)' },
            },
        ],
        notes: [
            {
                en: 'The past perfect works almost the same in both languages: it is the past before another past. The trapassato prossimo is the imperfetto of avere or essere plus the participle.',
                it: "Il past perfect funziona quasi allo stesso modo nelle due lingue: è il passato prima di un altro passato. Il trapassato prossimo è l'imperfetto di avere o essere più il participio.",
            },
            {
                en: "Italian uses the futuro anteriore in everyday speech, also for guesses about the past: 'Avrà dimenticato' means 'He has probably forgotten'. English would use 'probably'.",
                it: "L'italiano usa il futuro anteriore nel parlato di ogni giorno, anche per fare ipotesi sul passato: 'Avrà dimenticato' vuol dire 'He has probably forgotten'. L'inglese userebbe 'probably'.",
            },
            {
                en: "After quando or appena in a future sentence, Italian uses the futuro anteriore for the earlier action: 'Appena avrò finito, ti chiamerò'. English uses the present perfect: 'As soon as I have finished, I will call you'.",
                it: "Dopo quando o appena in una frase al futuro, l'italiano usa il futuro anteriore per l'azione precedente: 'Appena avrò finito, ti chiamerò'. L'inglese usa il present perfect: 'As soon as I have finished, I will call you'.",
            },
        ],
    },
    {
        id: 'ing-forms',
        level: 'B1',
        title: { en: 'The -ing form', it: "La forma in -ing" },
        summary: {
            en: 'What English does with -ing, and what Italian does instead.',
            it: "Cosa fa l'inglese con -ing, e cosa fa invece l'italiano.",
        },
        forms: {
            english: [
                {
                    name: 'Verb + -ing',
                    formula: {
                        en: 'Used as a noun (Swimming is fun), after prepositions (without saying goodbye) and after verbs like enjoy or finish. Also in time phrases: While walking, ...',
                        it: 'Usato come nome (Swimming is fun), dopo le preposizioni (without saying goodbye) e dopo verbi come enjoy o finish. Anche nelle espressioni di tempo: While walking, ...',
                    },
                },
            ],
            italian: [
                {
                    name: 'Infinito, gerundio, infinito passato',
                    formula: {
                        en: 'The infinitive does the noun job and follows prepositions: Nuotare è divertente. Senza salutare. The gerundio (camminando) means while or by doing. After means dopo + aver / essere + participle: Dopo aver mangiato.',
                        it: 'L\'infinito fa da nome e segue le preposizioni: Nuotare è divertente. Senza salutare. Il gerundio (camminando) vuol dire mentre o facendo. Dopo si dice dopo + aver / essere + participio: Dopo aver mangiato.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'Swimming is fun.', tag: '-ing as a noun' },
                italian: { text: 'Nuotare è divertente.', tag: 'Infinitive as a noun' },
            },
            {
                english: { text: 'She is good at cooking.', tag: 'preposition + -ing' },
                italian: { text: 'È brava a cucinare.', tag: 'preposition + infinitive' },
            },
            {
                english: { text: 'He left without saying goodbye.', tag: 'without + -ing' },
                italian: { text: 'È uscito senza salutare.', tag: 'senza + infinitive' },
            },
            {
                english: { text: 'He is interested in learning.', tag: 'in + -ing' },
                italian: { text: 'È interessato a imparare.', tag: 'a + infinitive' },
            },
            {
                english: { text: 'While walking, I listen to music.', tag: 'While + -ing' },
                italian: { text: 'Camminando, ascolto musica.', tag: 'Gerundio' },
            },
            {
                english: { text: 'After eating, she left.', tag: 'After + -ing' },
                italian: { text: 'Dopo aver mangiato, è uscita.', tag: 'dopo + past infinitive' },
            },
        ],
        notes: [
            {
                en: "After a preposition, Italian always uses the infinitive, never the gerundio: 'senza salutare', not 'senza salutando'.",
                it: "Dopo una preposizione, l'italiano usa sempre l'infinito, mai il gerundio: 'senza salutare', non 'senza salutando'.",
            },
            {
                en: "The Italian gerundio cannot be a noun. It is used for 'while / by doing' (Camminando, ascolto musica) and in stare + gerundio (Sto lavorando).",
                it: "Il gerundio italiano non può fare da nome. Si usa per 'mentre / facendo' (Camminando, ascolto musica) e in stare + gerundio (Sto lavorando).",
            },
            {
                en: "For 'after doing something', Italian uses dopo + aver / essere + participle: 'Dopo aver mangiato'. English just uses after + -ing.",
                it: "Per 'dopo aver fatto qualcosa', l'italiano usa dopo + aver / essere + participio: 'Dopo aver mangiato'. L'inglese usa solo after + -ing.",
            },
        ],
    },
];